import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { County } from '../../types/County.type';
import { TeamType } from '../../types/Team/TeamType.type';
import DatasetsMultiSelect from './DatasetsMultiSelect';

interface EditTeamModalProps {
  open: boolean;
  onClose: () => void;
  currentTeamName: string;
  currentCountyId: number;
  currentTeamTypeId: number;
  currentDatasetIds?: number[];
  counties: County[];
  teamTypes: TeamType[];
  onUpdate: (newName: string, newCountyId: number, newTeamTypeId: number, newDatasetIds: number[]) => void;
  loading: boolean;
}

const sameIds = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort((x, y) => x - y);
  return [...a].sort((x, y) => x - y).every((value, index) => value === sortedB[index]);
};

// Preselect county dataset if not already included in selected datasets
const countyDataset = (datasetIds: number[] | undefined, countyId: number): number[] =>
  datasetIds?.includes(countyId) ? datasetIds : [...(datasetIds ?? []), countyId];

function EditTeamModal({
  open,
  onClose,
  currentTeamName,
  currentCountyId,
  currentTeamTypeId,
  currentDatasetIds,
  counties,
  teamTypes,
  onUpdate,
  loading,
}: EditTeamModalProps) {
  const [newTeamName, setNewTeamName] = useState(currentTeamName);
  const [newTeamCountyId, setNewTeamCountyId] = useState(currentCountyId);
  const [newTeamTypeId, setNewTeamTypeId] = useState(currentTeamTypeId);
  const [newDatasetIds, setNewDatasetIds] = useState(countyDataset(currentDatasetIds, currentCountyId));

  const [prevOpen, setPrevOpen] = useState(open);
  if (open && !prevOpen) {
    setPrevOpen(open);
    setNewTeamName(currentTeamName);
    setNewTeamCountyId(currentCountyId);
    setNewTeamTypeId(currentTeamTypeId);
    setNewDatasetIds(countyDataset(currentDatasetIds, currentCountyId));
  }
  if (open !== prevOpen) {
    setPrevOpen(open);
  }

  const handleCountyChange = (countyId: number) => {
    setNewTeamCountyId(countyId);
    // Preselect dataset based on team country
    setNewDatasetIds((prev) => countyDataset(prev, countyId));
  };

  const hasChanges =
    newTeamName.trim() !== currentTeamName ||
    newTeamCountyId !== currentCountyId ||
    newTeamTypeId !== currentTeamTypeId ||
    !sameIds(newDatasetIds, currentDatasetIds ?? []);
  const handleUpdateClick = () => {
    if (newTeamName.trim() !== '' && hasChanges) {
      onUpdate(newTeamName.trim(), newTeamCountyId, newTeamTypeId, newDatasetIds);
    }
  };

  const handleModalClose = () => {
    setNewTeamName(currentTeamName);
    setNewTeamCountyId(currentCountyId);
    setNewTeamTypeId(currentTeamTypeId);
    setNewDatasetIds(countyDataset(currentDatasetIds, currentCountyId));
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      fullWidth
      maxWidth='xs'
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (e: React.FormEvent) => {
            e.preventDefault();
            handleUpdateClick();
          },
        },
      }}
    >
      <DialogTitle>Edit Team</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label='Team name'
            type='text'
            fullWidth
            variant='outlined'
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            sx={{ '& .MuiInputLabel-root': { fontSize: 14 } }}
          />
          <FormControl fullWidth>
            <InputLabel id='edit-county-label' sx={{ fontSize: '14px' }}>
              County
            </InputLabel>
            <Select
              labelId='edit-county-label'
              value={newTeamCountyId}
              label='County'
              onChange={(e) => handleCountyChange(Number(e.target.value))}
            >
              {counties?.map((county: County) => (
                <MenuItem key={county.id} value={county.id}>
                  {county.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatasetsMultiSelect
            counties={counties}
            value={newDatasetIds}
            lockedCountyId={newTeamCountyId}
            onChange={setNewDatasetIds}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id='edit-team-type-label' sx={{ fontSize: '14px' }}>
              Team Type
            </InputLabel>
            <Select
              labelId='edit-team-type-label'
              value={newTeamTypeId}
              label='Team Type'
              onChange={(e) => setNewTeamTypeId(Number(e.target.value))}
            >
              {teamTypes?.map((teamType: TeamType) => (
                <MenuItem key={teamType.id} value={teamType.id}>
                  {teamType.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleModalClose} disabled={loading}>
          Cancel
        </Button>

        <Button
          variant='contained'
          disableElevation
          disabled={loading || newTeamName.trim() === '' || !hasChanges}
          onClick={handleUpdateClick}
        >
          {loading ? 'Please wait...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditTeamModal;
